import React, { useState, useEffect, useRef } from 'react';
import { Button, Typography, Radio } from 'antd';
import { ReloadOutlined, TrophyOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

const TicTacToe = ({ onGameComplete }) => {
    const [board, setBoard] = useState(Array(9).fill(null));
    const [isXNext, setIsXNext] = useState(true);
    const [winner, setWinner] = useState(null);
    const [showStartScreen, setShowStartScreen] = useState(true);
    const [showModeSelect, setShowModeSelect] = useState(false);
    const [showCharacterSelect, setShowCharacterSelect] = useState(false);
    const [gameMode, setGameMode] = useState(null); // 'regular' or 'custom'
    const [playerChar, setPlayerChar] = useState('X');
    const [aiChar, setAiChar] = useState('O');
    const [gameStarted, setGameStarted] = useState(false);
    const [gameEnded, setGameEnded] = useState(false);
    const [showResults, setShowResults] = useState(false);
    const [playerScore, setPlayerScore] = useState(0);
    const [aiScore, setAiScore] = useState(0);
    const [difficulty, setDifficulty] = useState('medium'); // 'easy', 'medium', 'hard'
    
    // Audio refs
    const clickAudioRef = useRef(null);
    const snapAudioRef = useRef(null);
    const winnerAudioRef = useRef(null);
    const loserAudioRef = useRef(null);

    // Livestock characters for custom mode
    const livestockCharacters = [
        { emoji: '🐄', name: 'Cow' },
        { emoji: '🐂', name: 'Ox' },
        { emoji: '🐃', name: 'Water Buffalo' },
        { emoji: '🐑', name: 'Sheep' },
        { emoji: '🐐', name: 'Goat' },
        { emoji: '🐎', name: 'Horse' },
        { emoji: '🐴', name: 'Horse Face' },
        { emoji: '🫏', name: 'Donkey' },
        { emoji: '🐔', name: 'Chicken' },
        { emoji: '🐓', name: 'Rooster' },
        { emoji: '🦆', name: 'Duck' },
        { emoji: '🦃', name: 'Turkey' }
    ];

    // Check for winner and return winning line indices
    const calculateWinner = (squares) => {
        const lines = [
            [0, 1, 2],
            [3, 4, 5],
            [6, 7, 8],
            [0, 3, 6],
            [1, 4, 7],
            [2, 5, 8],
            [0, 4, 8],
            [2, 4, 6],
        ];
        for (let i = 0; i < lines.length; i++) {
            const [a, b, c] = lines[i];
            if (squares[a] && squares[a] === squares[b] && squares[a] === squares[c]) {
                return squares[a];
            }
        }
        return null;
    };
    
    // Get winning line indices
    const getWinningLine = (squares) => {
        const lines = [
            [0, 1, 2],
            [3, 4, 5],
            [6, 7, 8],
            [0, 3, 6],
            [1, 4, 7],
            [2, 5, 8],
            [0, 4, 8],
            [2, 4, 6],
        ];
        for (let i = 0; i < lines.length; i++) {
            const [a, b, c] = lines[i];
            if (squares[a] && squares[a] === squares[b] && squares[a] === squares[c]) {
                return [a, b, c];
            }
        }
        return null;
    };
    
    // Get display value for a square
    const getSquareValue = (value) => {
        if (!value) return null;
        if (gameMode === 'custom') {
            return value === playerChar ? playerChar : aiChar;
        }
        return value === 'X' ? 'X' : 'O';
    };

    // Check if board is full
    const isBoardFull = (squares) => {
        return squares.every(square => square !== null);
    };

    // Minimax algorithm for Hard difficulty
    const minimax = (squares, depth, isMaximizing, alpha = -Infinity, beta = Infinity) => {
        const winner = calculateWinner(squares);
        
        if (winner === aiChar) return 10 - depth;
        if (winner === playerChar) return depth - 10;
        if (isBoardFull(squares)) return 0;
        
        if (isMaximizing) {
            let maxEval = -Infinity;
            for (let i = 0; i < 9; i++) {
                if (squares[i] === null) {
                    squares[i] = aiChar;
                    const evaluation = minimax(squares, depth + 1, false, alpha, beta);
                    squares[i] = null;
                    maxEval = Math.max(maxEval, evaluation);
                    alpha = Math.max(alpha, evaluation);
                    if (beta <= alpha) break; // Alpha-beta pruning
                }
            }
            return maxEval;
        } else {
            let minEval = Infinity;
            for (let i = 0; i < 9; i++) {
                if (squares[i] === null) {
                    squares[i] = playerChar;
                    const evaluation = minimax(squares, depth + 1, true, alpha, beta);
                    squares[i] = null;
                    minEval = Math.min(minEval, evaluation);
                    beta = Math.min(beta, evaluation);
                    if (beta <= alpha) break; // Alpha-beta pruning
                }
            }
            return minEval;
        }
    };

    // AI move with difficulty levels
    const makeAIMove = (squares) => {
        if (difficulty === 'hard') {
            // Hard: Use minimax for optimal play
            let bestMove = -1;
            let bestValue = -Infinity;
            
            for (let i = 0; i < 9; i++) {
                if (squares[i] === null) {
                    squares[i] = aiChar;
                    const moveValue = minimax(squares, 0, false);
                    squares[i] = null;
                    
                    if (moveValue > bestValue) {
                        bestValue = moveValue;
                        bestMove = i;
                    }
                }
            }
            return bestMove;
        } else if (difficulty === 'medium') {
            // Medium: Sometimes makes mistakes to give player a chance to win
            const winRandom = Math.random();
            const blockRandom = Math.random();
            
            // Check if AI can win - but only take it 75% of the time
            if (winRandom < 0.75) {
                for (let i = 0; i < 9; i++) {
                    if (squares[i] === null) {
                        const testSquares = [...squares];
                        testSquares[i] = aiChar;
                        if (calculateWinner(testSquares) === aiChar) {
                            return i;
                        }
                    }
                }
            }
            
            // Check if need to block player - but only block 75% of the time
            if (blockRandom < 0.75) {
                for (let i = 0; i < 9; i++) {
                    if (squares[i] === null) {
                        const testSquares = [...squares];
                        testSquares[i] = playerChar;
                        if (calculateWinner(testSquares) === playerChar) {
                            return i;
                        }
                    }
                }
            }
            
            // Take center if available
            if (squares[4] === null) {
                return 4;
            }
            
            // Take a corner
            const corners = [0, 2, 6, 8];
            const availableCorners = corners.filter(i => squares[i] === null);
            if (availableCorners.length > 0) {
                return availableCorners[Math.floor(Math.random() * availableCorners.length)];
            }
            
            // Take any available spot
            const available = squares.map((square, index) => square === null ? index : null).filter(val => val !== null);
            return available[Math.floor(Math.random() * available.length)];
        } else {
            // Easy: Sometimes makes random or suboptimal moves
            const random = Math.random();
            
            // 30% chance to make a good move (block or win)
            if (random < 0.3) {
                // Check if AI can win
                for (let i = 0; i < 9; i++) {
                    if (squares[i] === null) {
                        const testSquares = [...squares];
                        testSquares[i] = aiChar;
                        if (calculateWinner(testSquares) === aiChar) {
                            return i;
                        }
                    }
                }
                
                // Check if need to block player (only 50% of the time)
                if (random < 0.15) {
                    for (let i = 0; i < 9; i++) {
                        if (squares[i] === null) {
                            const testSquares = [...squares];
                            testSquares[i] = playerChar;
                            if (calculateWinner(testSquares) === playerChar) {
                                return i;
                            }
                        }
                    }
                }
            }
            
            // Otherwise, make a random move
            const available = squares.map((square, index) => square === null ? index : null).filter(val => val !== null);
            return available[Math.floor(Math.random() * available.length)];
        }
    };

    const handleClick = (index) => {
        if (board[index] || winner || !isXNext) {
            return;
        }

        // Play click sound when player clicks
        if (clickAudioRef.current) {
            try {
                clickAudioRef.current.currentTime = 0; // Reset to start
                clickAudioRef.current.play().catch(error => {
                    // Handle autoplay restrictions
                    console.log('Could not play click sound:', error);
                });
            } catch (error) {
                console.log('Error playing click sound:', error);
            }
        }

        const newBoard = [...board];
        newBoard[index] = playerChar;
        setBoard(newBoard);
        
        const newWinner = calculateWinner(newBoard);
        if (newWinner) {
            setWinner(newWinner);
            setGameEnded(true);
            if (newWinner === playerChar) {
                setPlayerScore(prev => prev + 1);
                // Play winner sound
                if (winnerAudioRef.current) {
                    try {
                        winnerAudioRef.current.currentTime = 0;
                        winnerAudioRef.current.play().catch(error => {
                            console.log('Could not play winner sound:', error);
                        });
                    } catch (error) {
                        console.log('Error playing winner sound:', error);
                    }
                }
            } else {
                setAiScore(prev => prev + 1);
                // Play loser sound
                if (loserAudioRef.current) {
                    try {
                        loserAudioRef.current.currentTime = 0;
                        loserAudioRef.current.play().catch(error => {
                            console.log('Could not play loser sound:', error);
                        });
                    } catch (error) {
                        console.log('Error playing loser sound:', error);
                    }
                }
            }
            if (onGameComplete) {
                setTimeout(() => onGameComplete(newWinner === playerChar ? 100 : 0), 1000);
            }
        } else if (isBoardFull(newBoard)) {
            setGameEnded(true);
            if (onGameComplete) {
                setTimeout(() => onGameComplete(50), 1000);
            }
        } else {
            setIsXNext(false);
        }
    };

    // AI move effect
    useEffect(() => {
        if (!isXNext && !winner && !gameEnded && gameStarted) {
            const timer = setTimeout(() => {
                const aiMove = makeAIMove(board);
                if (aiMove !== undefined && board[aiMove] === null) {
                    // Play snap sound for AI move
                    if (snapAudioRef.current) {
                        try {
                            snapAudioRef.current.currentTime = 0; // Reset to start
                            snapAudioRef.current.play().catch(error => {
                                // Handle autoplay restrictions
                                console.log('Could not play snap sound:', error);
                            });
                        } catch (error) {
                            console.log('Error playing snap sound:', error);
                        }
                    }
                    
                    const newBoard = [...board];
                    newBoard[aiMove] = aiChar;
                    setBoard(newBoard);
                    
                    const newWinner = calculateWinner(newBoard);
                    if (newWinner) {
                        setWinner(newWinner);
                        setGameEnded(true);
                        setAiScore(prev => prev + 1);
                        // Play loser sound when AI wins
                        if (loserAudioRef.current) {
                            try {
                                loserAudioRef.current.currentTime = 0;
                                loserAudioRef.current.play().catch(error => {
                                    console.log('Could not play loser sound:', error);
                                });
                            } catch (error) {
                                console.log('Error playing loser sound:', error);
                            }
                        }
                        if (onGameComplete) {
                            setTimeout(() => onGameComplete(0), 1000);
                        }
                    } else if (isBoardFull(newBoard)) {
                        setGameEnded(true);
                        if (onGameComplete) {
                            setTimeout(() => onGameComplete(50), 1000);
                        }
                    } else {
                        setIsXNext(true);
                    }
                }
            }, difficulty === 'hard' ? 800 : 500); // Slightly longer delay for hard mode to show "thinking"
            return () => clearTimeout(timer);
        }
    }, [isXNext, board, winner, gameEnded, gameStarted, difficulty, playerChar, aiChar]);

    const resetGame = () => {
        setBoard(Array(9).fill(null));
        setIsXNext(true);
        setWinner(null);
        setGameEnded(false);
        setShowResults(false);
    };

    // Initialize audio files
    useEffect(() => {
        clickAudioRef.current = new Audio('/sounds/mouse-click.mp3');
        clickAudioRef.current.volume = 0.6; // Set volume to 60%
        
        snapAudioRef.current = new Audio('/sounds/snap.mp3');
        snapAudioRef.current.volume = 0.6; // Set volume to 60%
        
        winnerAudioRef.current = new Audio('/sounds/winner.mp3');
        winnerAudioRef.current.volume = 0.7; // Set volume to 70%
        
        loserAudioRef.current = new Audio('/sounds/loser.mp3');
        loserAudioRef.current.volume = 0.7; // Set volume to 70%
        
        return () => {
            if (clickAudioRef.current) {
                clickAudioRef.current.pause();
                clickAudioRef.current = null;
            }
            if (snapAudioRef.current) {
                snapAudioRef.current.pause();
                snapAudioRef.current = null;
            }
            if (winnerAudioRef.current) {
                winnerAudioRef.current.pause();
                winnerAudioRef.current = null;
            }
            if (loserAudioRef.current) {
                loserAudioRef.current.pause();
                loserAudioRef.current = null;
            }
        };
    }, []);

    // Show results after 1 second when game ends
    useEffect(() => {
        if (gameEnded) {
            const timer = setTimeout(() => {
                setShowResults(true);
            }, 1000);
            return () => clearTimeout(timer);
        } else {
            setShowResults(false);
        }
    }, [gameEnded]);

    // Automatically start a new game immediately after showing results briefly
    useEffect(() => {
        if (showResults && gameStarted) {
            const timer = setTimeout(() => {
                resetGame();
            }, 2000); // Start new game after 2 seconds of showing results
            return () => clearTimeout(timer);
        }
    }, [showResults, gameStarted]);

    const startGame = () => {
        if (gameMode === 'custom' && (!playerChar || !aiChar)) {
            return; // Don't start if characters not selected
        }
        setShowModeSelect(false);
        setShowCharacterSelect(false);
        setGameStarted(true);
        setGameEnded(false);
        setShowResults(false);
        resetGame();
    };
    
    const selectGameMode = (mode) => {
        setShowStartScreen(false);
        setGameMode(mode);
        if (mode === 'regular') {
            setPlayerChar('X');
            setAiChar('O');
            setShowModeSelect(false);
        } else {
            setShowModeSelect(false);
            setShowCharacterSelect(true);
        }
    };
    
    const selectPlayerCharacter = (character) => {
        // Prevent selecting the same emoji as AI
        if (character.emoji === aiChar) {
            return;
        }
        setPlayerChar(character.emoji);
    };
    
    const selectAICharacter = (character) => {
        // Prevent selecting the same emoji as player
        if (character.emoji === playerChar) {
            return;
        }
        setAiChar(character.emoji);
    };

    const handleDifficultyChange = (e) => {
        setDifficulty(e.target.value);
        if (gameStarted) {
            resetGame();
        }
    };

    const renderSquare = (index) => {
        const value = board[index];
        const winningLine = winner ? getWinningLine(board) : null;
        const isWinning = winningLine && winningLine.includes(index);
        const isAiWinner = winner === aiChar;
        const displayValue = getSquareValue(value);
        
        return (
            <button
                className={`square ${isWinning ? (isAiWinner ? 'winning-ai' : 'winning') : ''} ${value ? 'filled' : ''}`}
                onClick={() => handleClick(index)}
                disabled={!!winner || gameEnded || !isXNext}
            >
                {gameMode === 'custom' ? (
                    displayValue ? (
                        <span className={value === playerChar ? 'player-mark' : 'ai-mark'} style={{ fontSize: '48px' }}>
                            {displayValue}
                        </span>
                    ) : null
                ) : (
                    value === 'X' ? (
                    <span className="x-mark">✕</span>
                ) : value === 'O' ? (
                    <span className="o-mark">○</span>
                    ) : null
                )}
            </button>
        );
    };

    const getStatus = () => {
        if (winner) {
            return winner === playerChar ? '🎉 You Win!' : '😢 You Lost!';
        }
        if (gameEnded && !winner) {
            return '🤝 It\'s a Draw!';
        }
        return isXNext ? `Your Turn (${playerChar})` : 'AI Thinking...';
    };

    // Splash Screen with Image
    if (showStartScreen && !showModeSelect && !showCharacterSelect) {
        return (
            <div style={{
                textAlign: 'center',
                padding: '0',
                background: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                border: '1px solid rgba(24, 144, 255, 0.2)',
                overflow: 'hidden'
            }}>
                {/* Game Preview Image */}
                <div style={{
                    width: '100%',
                    height: '400px',
                    background: 'linear-gradient(180deg, #87CEEB 0%, #E0F6FF 30%, #98D8C8 70%, #90EE90 100%)',
                    position: 'relative',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <img 
                        src="/assets/images/tic-tac-hoof.jpg" 
                        alt="Tic Tac Hoof Game"
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            objectPosition: 'center'
                        }}
                        onError={(e) => {
                            // Fallback if image doesn't exist
                            e.target.style.display = 'none';
                        }}
                    />
                </div>
                
                {/* Start Button Section */}
                <div style={{
                    padding: '20px 16px',
                    background: 'rgba(255, 255, 255, 0.95)'
                }}>
                    <Title level={5} style={{ marginBottom: '10px', color: '#1890ff', fontSize: '16px' }}>
                        🎮 Tic-Tac-Hoof
                    </Title>
                    <Text style={{ 
                        fontSize: '13px', 
                        color: '#666',
                        display: 'block',
                        marginBottom: '12px'
                    }}>
                        Play against AI! Choose Regular or Custom characters.
                    </Text>
                    <Button
                        type="primary"
                        size="middle"
                        icon={<TrophyOutlined />}
                        onClick={() => {
                            setShowStartScreen(false);
                            setShowModeSelect(true);
                        }}
                        style={{
                            height: '36px',
                            fontSize: '13px',
                            fontWeight: 600,
                            borderRadius: '8px',
                            padding: '0 20px',
                            background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                            border: 'none',
                            boxShadow: '0 4px 12px rgba(24, 144, 255, 0.3)'
                        }}
                    >
                        Start Game
                    </Button>
                </div>
            </div>
        );
    }

    // Game Mode Selection Screen
    if (showModeSelect && !showCharacterSelect) {
        return (
            <div style={{
                textAlign: 'center',
                padding: '20px 16px',
                background: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                border: '1px solid rgba(24, 144, 255, 0.2)'
            }}>
                <Title level={5} style={{ marginBottom: '10px', color: '#1890ff', fontSize: '18px' }}>
                    🎮 Tic-Tac-Hoof
                </Title>
                <Text style={{ 
                    fontSize: '14px', 
                    color: '#666',
                    display: 'block',
                    marginBottom: '20px'
                }}>
                    Choose your game mode
                </Text>
                
                <div style={{ 
                    display: 'flex',
                    gap: '16px',
                    justifyContent: 'center',
                    marginBottom: '20px',
                    flexWrap: 'wrap'
                }}>
                    <Button
                        type="primary"
                        size="large"
                        onClick={() => selectGameMode('regular')}
                        style={{
                            height: '120px',
                            width: '140px',
                            fontSize: '16px',
                            fontWeight: 600,
                            borderRadius: '12px',
                            background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                            border: 'none',
                            boxShadow: '0 4px 12px rgba(24, 144, 255, 0.3)',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                        }}
                    >
                        <div style={{ fontSize: '40px' }}>✕</div>
                        <div>Regular</div>
                        <div style={{ fontSize: '12px', opacity: 0.9 }}>Classic X and O</div>
                    </Button>
                    
                    <Button
                        type="default"
                        size="large"
                        onClick={() => selectGameMode('custom')}
                        style={{
                            height: '120px',
                            width: '140px',
                            fontSize: '16px',
                            fontWeight: 600,
                            borderRadius: '12px',
                            background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
                            border: 'none',
                            color: 'white',
                            boxShadow: '0 4px 12px rgba(82, 196, 26, 0.3)',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                        }}
                    >
                        <div style={{ fontSize: '40px' }}>🐄</div>
                        <div>Custom</div>
                        <div style={{ fontSize: '12px', opacity: 0.9 }}>Choose Characters</div>
                    </Button>
                </div>
            </div>
        );
    }
    
    // Character Selection Screen
    if (showCharacterSelect) {
        return (
            <div style={{
                textAlign: 'center',
                padding: '20px 16px',
                background: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                border: '1px solid rgba(24, 144, 255, 0.2)'
            }}>
                <Title level={5} style={{ marginBottom: '10px', color: '#1890ff', fontSize: '18px' }}>
                    🐄 Select Your Characters
                </Title>
                
                {/* Player Character Selection */}
                <div style={{ marginBottom: '24px' }}>
                    <Text style={{ 
                        fontSize: '14px', 
                        color: '#666',
                        display: 'block',
                        marginBottom: '12px',
                        fontWeight: 600
                    }}>
                        Your Character:
                    </Text>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(4, 1fr)',
                        gap: '8px',
                        marginBottom: '12px'
                    }}>
                        {livestockCharacters.map((character, index) => {
                            const isSelected = playerChar === character.emoji;
                            const isDisabled = character.emoji === aiChar;
                            
                            return (
                                <div
                                    key={index}
                                    onClick={() => !isDisabled && selectPlayerCharacter(character)}
                                    style={{
                                        padding: '12px',
                                        borderRadius: '10px',
                                        border: isSelected 
                                            ? '3px solid #1890ff' 
                                            : isDisabled
                                            ? '2px solid #ff4d4f'
                                            : '2px solid #d9d9d9',
                                        background: isSelected
                                            ? 'linear-gradient(135deg, rgba(24, 144, 255, 0.1) 0%, rgba(24, 144, 255, 0.05) 100%)'
                                            : isDisabled
                                            ? 'linear-gradient(135deg, rgba(255, 77, 79, 0.1) 0%, rgba(255, 77, 79, 0.05) 100%)'
                                            : 'white',
                                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                                        transition: 'all 0.3s ease',
                                        transform: isSelected ? 'scale(1.05)' : 'scale(1)',
                                        boxShadow: isSelected
                                            ? '0 4px 12px rgba(24, 144, 255, 0.3)'
                                            : '0 2px 4px rgba(0, 0, 0, 0.1)',
                                        opacity: isDisabled ? 0.5 : 1
                                    }}
                                    title={isDisabled ? 'Already selected by AI' : ''}
                                >
                                    <div style={{ fontSize: '32px', marginBottom: '4px' }}>
                                        {character.emoji}
                                    </div>
                                    <Text style={{ 
                                        fontSize: '10px', 
                                        color: isSelected ? '#1890ff' : isDisabled ? '#ff4d4f' : '#666'
                                    }}>
                                        {character.name}
                                        {isDisabled && ' (AI)'}
                                    </Text>
                                </div>
                            );
                        })}
                    </div>
                </div>
                
                {/* AI Character Selection */}
                <div style={{ marginBottom: '24px' }}>
                    <Text style={{ 
                        fontSize: '14px', 
                        color: '#666',
                        display: 'block',
                        marginBottom: '12px',
                        fontWeight: 600
                    }}>
                        AI Character:
                    </Text>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(4, 1fr)',
                        gap: '8px',
                        marginBottom: '12px'
                    }}>
                        {livestockCharacters.map((character, index) => {
                            const isSelected = aiChar === character.emoji;
                            const isDisabled = character.emoji === playerChar;
                            
                            return (
                                <div
                                    key={index}
                                    onClick={() => !isDisabled && selectAICharacter(character)}
                                    style={{
                                        padding: '12px',
                                        borderRadius: '10px',
                                        border: isSelected 
                                            ? '3px solid #52c41a' 
                                            : isDisabled
                                            ? '2px solid #ff4d4f'
                                            : '2px solid #d9d9d9',
                                        background: isSelected
                                            ? 'linear-gradient(135deg, rgba(82, 196, 26, 0.1) 0%, rgba(82, 196, 26, 0.05) 100%)'
                                            : isDisabled
                                            ? 'linear-gradient(135deg, rgba(255, 77, 79, 0.1) 0%, rgba(255, 77, 79, 0.05) 100%)'
                                            : 'white',
                                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                                        transition: 'all 0.3s ease',
                                        transform: isSelected ? 'scale(1.05)' : 'scale(1)',
                                        boxShadow: isSelected
                                            ? '0 4px 12px rgba(82, 196, 26, 0.3)'
                                            : '0 2px 4px rgba(0, 0, 0, 0.1)',
                                        opacity: isDisabled ? 0.5 : 1
                                    }}
                                    title={isDisabled ? 'Already selected by Player' : ''}
                                >
                                    <div style={{ fontSize: '32px', marginBottom: '4px' }}>
                                        {character.emoji}
                                    </div>
                                    <Text style={{ 
                                        fontSize: '10px', 
                                        color: isSelected ? '#52c41a' : isDisabled ? '#ff4d4f' : '#666'
                                    }}>
                                        {character.name}
                                        {isDisabled && ' (You)'}
                                    </Text>
                                </div>
                            );
                        })}
                    </div>
                </div>
                
                {/* Difficulty Selector */}
                <div style={{ marginBottom: '20px' }}>
                    <Text style={{ 
                        fontSize: '13px', 
                        color: '#666',
                        display: 'block',
                        marginBottom: '12px',
                        fontWeight: 600
                    }}>
                        AI Difficulty:
                    </Text>
                    <div style={{ 
                        display: 'flex',
                        justifyContent: 'center',
                        gap: '12px',
                        flexWrap: 'wrap'
                    }}>
                        {['easy', 'medium', 'hard'].map((diff) => (
                            <div
                                key={diff}
                                onClick={() => setDifficulty(diff)}
                                style={{
                                    flex: '1',
                                    minWidth: '80px',
                                    maxWidth: '100px',
                                    padding: '12px 8px',
                                    borderRadius: '10px',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    background: difficulty === diff 
                                        ? diff === 'easy' ? 'linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%)'
                                        : diff === 'medium' ? 'linear-gradient(135deg, #e6f7ff 0%, #bae7ff 100%)'
                                        : 'linear-gradient(135deg, #fff1f0 0%, #ffccc7 100%)'
                                        : 'white',
                                    border: difficulty === diff 
                                        ? `2px solid ${diff === 'easy' ? '#52c41a' : diff === 'medium' ? '#1890ff' : '#ff4d4f'}` 
                                        : '2px solid #e8e8e8',
                                    boxShadow: difficulty === diff
                                        ? `0 4px 12px rgba(${diff === 'easy' ? '82, 196, 26' : diff === 'medium' ? '24, 144, 255' : '255, 77, 79'}, 0.2)`
                                        : '0 2px 4px rgba(0, 0, 0, 0.1)'
                                }}
                            >
                                <div style={{ fontSize: '24px', marginBottom: '4px' }}>
                                    {diff === 'easy' ? '😊' : diff === 'medium' ? '🤔' : '🔥'}
                                </div>
                                <Text style={{ 
                                    fontSize: '12px', 
                                    fontWeight: 600,
                                    color: difficulty === diff 
                                        ? diff === 'easy' ? '#52c41a' : diff === 'medium' ? '#1890ff' : '#ff4d4f'
                                        : '#666'
                                }}>
                                    {diff.charAt(0).toUpperCase() + diff.slice(1)}
                                </Text>
                            </div>
                        ))}
                    </div>
                </div>
                
                <Button
                    type="primary"
                    size="large"
                    onClick={startGame}
                    disabled={!playerChar || !aiChar || playerChar === aiChar}
                    style={{
                        height: '44px',
                        fontSize: '15px',
                        fontWeight: 600,
                        borderRadius: '10px',
                        padding: '0 24px',
                        background: (playerChar && aiChar && playerChar !== aiChar)
                            ? 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)'
                            : 'linear-gradient(135deg, #d9d9d9 0%, #bfbfbf 100%)',
                        border: 'none',
                        boxShadow: (playerChar && aiChar && playerChar !== aiChar)
                            ? '0 4px 12px rgba(24, 144, 255, 0.3)'
                            : 'none',
                        cursor: (playerChar && aiChar && playerChar !== aiChar) ? 'pointer' : 'not-allowed'
                    }}
                >
                    🤖 Start Game
                </Button>
                {playerChar === aiChar && playerChar && aiChar && (
                    <Text style={{ 
                        fontSize: '12px', 
                        color: '#ff4d4f',
                        display: 'block',
                        marginTop: '8px',
                        fontWeight: 600
                    }}>
                        ⚠️ Player and AI must have different characters!
                    </Text>
                )}
            </div>
        );
    }
    
    if (!gameStarted && gameMode === 'regular') {
        return (
            <div style={{
                textAlign: 'center',
                padding: '20px 16px',
                background: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                border: '1px solid rgba(24, 144, 255, 0.2)'
            }}>
                <Text style={{ 
                    fontSize: '14px', 
                    color: '#666',
                    display: 'block',
                    marginBottom: '16px'
                }}>
                    Play against AI. You are {playerChar}, AI is {aiChar}.
                </Text>
                
                {/* Difficulty Selector */}
                <div style={{ marginBottom: '20px' }}>
                    <Text style={{ 
                        fontSize: '13px', 
                        color: '#666',
                        display: 'block',
                        marginBottom: '12px',
                        fontWeight: 600
                    }}>
                        AI Difficulty:
                    </Text>
                    <div style={{ 
                        display: 'flex',
                        justifyContent: 'center',
                        gap: '12px',
                        flexWrap: 'wrap'
                    }}>
                        {/* Easy Button */}
                        <div
                            className={`difficulty-card ${difficulty === 'easy' ? 'selected' : ''}`}
                            onClick={() => handleDifficultyChange({ target: { value: 'easy' } })}
                            style={{
                                flex: '1',
                                minWidth: '100px',
                                maxWidth: '140px',
                                padding: '16px 12px',
                                borderRadius: '12px',
                                cursor: 'pointer',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                background: difficulty === 'easy' 
                                    ? 'linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%)'
                                    : 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)',
                                border: difficulty === 'easy' 
                                    ? '2px solid #52c41a' 
                                    : '2px solid #e8e8e8',
                                boxShadow: difficulty === 'easy'
                                    ? '0 8px 20px rgba(82, 196, 26, 0.25)'
                                    : '0 2px 8px rgba(0, 0, 0, 0.08)',
                                transform: difficulty === 'easy' ? 'translateY(-2px)' : 'translateY(0)',
                                position: 'relative',
                                overflow: 'hidden'
                            }}
                        >
                            <div style={{
                                fontSize: '32px',
                                marginBottom: '8px',
                                transition: 'transform 0.3s ease'
                            }}>
                                😊
                            </div>
                            <div style={{
                                fontSize: '14px',
                                fontWeight: 700,
                                color: difficulty === 'easy' ? '#52c41a' : '#666',
                                marginBottom: '4px'
                            }}>
                                Easy
                            </div>
                            <div style={{
                                fontSize: '11px',
                                color: difficulty === 'easy' ? '#73d13d' : '#999',
                                fontWeight: 500
                            }}>
                                Beginner Friendly
                            </div>
                            {difficulty === 'easy' && (
                                <div style={{
                                    position: 'absolute',
                                    top: '8px',
                                    right: '8px',
                                    width: '20px',
                                    height: '20px',
                                    borderRadius: '50%',
                                    background: '#52c41a',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '12px',
                                    color: 'white',
                                    fontWeight: 'bold'
                                }}>
                                    ✓
                                </div>
                            )}
                        </div>

                        {/* Medium Button */}
                        <div
                            className={`difficulty-card ${difficulty === 'medium' ? 'selected' : ''}`}
                            onClick={() => handleDifficultyChange({ target: { value: 'medium' } })}
                            style={{
                                flex: '1',
                                minWidth: '100px',
                                maxWidth: '140px',
                                padding: '16px 12px',
                                borderRadius: '12px',
                                cursor: 'pointer',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                background: difficulty === 'medium' 
                                    ? 'linear-gradient(135deg, #e6f7ff 0%, #bae7ff 100%)'
                                    : 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)',
                                border: difficulty === 'medium' 
                                    ? '2px solid #1890ff' 
                                    : '2px solid #e8e8e8',
                                boxShadow: difficulty === 'medium'
                                    ? '0 8px 20px rgba(24, 144, 255, 0.25)'
                                    : '0 2px 8px rgba(0, 0, 0, 0.08)',
                                transform: difficulty === 'medium' ? 'translateY(-2px)' : 'translateY(0)',
                                position: 'relative',
                                overflow: 'hidden'
                            }}
                        >
                            <div style={{
                                fontSize: '32px',
                                marginBottom: '8px',
                                transition: 'transform 0.3s ease'
                            }}>
                                🤔
                            </div>
                            <div style={{
                                fontSize: '14px',
                                fontWeight: 700,
                                color: difficulty === 'medium' ? '#1890ff' : '#666',
                                marginBottom: '4px'
                            }}>
                                Medium
                            </div>
                            <div style={{
                                fontSize: '11px',
                                color: difficulty === 'medium' ? '#40a9ff' : '#999',
                                fontWeight: 500
                            }}>
                                Balanced Challenge
                            </div>
                            {difficulty === 'medium' && (
                                <div style={{
                                    position: 'absolute',
                                    top: '8px',
                                    right: '8px',
                                    width: '20px',
                                    height: '20px',
                                    borderRadius: '50%',
                                    background: '#1890ff',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '12px',
                                    color: 'white',
                                    fontWeight: 'bold'
                                }}>
                                    ✓
                                </div>
                            )}
                        </div>

                        {/* Hard Button */}
                        <div
                            className={`difficulty-card ${difficulty === 'hard' ? 'selected' : ''}`}
                            onClick={() => handleDifficultyChange({ target: { value: 'hard' } })}
                            style={{
                                flex: '1',
                                minWidth: '100px',
                                maxWidth: '140px',
                                padding: '16px 12px',
                                borderRadius: '12px',
                                cursor: 'pointer',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                background: difficulty === 'hard' 
                                    ? 'linear-gradient(135deg, #fff1f0 0%, #ffccc7 100%)'
                                    : 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)',
                                border: difficulty === 'hard' 
                                    ? '2px solid #ff4d4f' 
                                    : '2px solid #e8e8e8',
                                boxShadow: difficulty === 'hard'
                                    ? '0 8px 20px rgba(255, 77, 79, 0.25)'
                                    : '0 2px 8px rgba(0, 0, 0, 0.08)',
                                transform: difficulty === 'hard' ? 'translateY(-2px)' : 'translateY(0)',
                                position: 'relative',
                                overflow: 'hidden'
                            }}
                        >
                            <div style={{
                                fontSize: '32px',
                                marginBottom: '8px',
                                transition: 'transform 0.3s ease'
                            }}>
                                🔥
                            </div>
                            <div style={{
                                fontSize: '14px',
                                fontWeight: 700,
                                color: difficulty === 'hard' ? '#ff4d4f' : '#666',
                                marginBottom: '4px'
                            }}>
                                Hard
                            </div>
                            <div style={{
                                fontSize: '11px',
                                color: difficulty === 'hard' ? '#ff7875' : '#999',
                                fontWeight: 500
                            }}>
                                Expert Level
                            </div>
                            {difficulty === 'hard' && (
                                <div style={{
                                    position: 'absolute',
                                    top: '8px',
                                    right: '8px',
                                    width: '20px',
                                    height: '20px',
                                    borderRadius: '50%',
                                    background: '#ff4d4f',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '12px',
                                    color: 'white',
                                    fontWeight: 'bold'
                                }}>
                                    ✓
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                
                <Button
                    type="primary"
                    size="large"
                    onClick={startGame}
                    style={{
                        height: '44px',
                        fontSize: '15px',
                        fontWeight: 600,
                        borderRadius: '10px',
                        padding: '0 24px',
                        background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                        border: 'none',
                        boxShadow: '0 4px 12px rgba(24, 144, 255, 0.3)'
                    }}
                >
                    🤖 Start Game
                </Button>
            </div>
        );
    }

    return (
        <div style={{ width: '100%', marginTop: '0', paddingTop: '0', position: 'relative' }}>
            {/* Backdrop Overlay */}
            {showResults && (
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.3)',
                    zIndex: 50,
                    borderRadius: '12px'
                }} />
            )}

            {/* Game Over Actions - Overlay in Middle */}
            {showResults && (
                <div className="game-over-actions" style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 100,
                    textAlign: 'center',
                    padding: '16px 20px',
                    background: 'linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%)',
                    borderRadius: '12px',
                    border: '2px solid #52c41a',
                    boxShadow: '0 8px 24px rgba(82, 196, 26, 0.3)',
                    maxWidth: '320px',
                    width: 'calc(100% - 32px)'
                }}>
                    <TrophyOutlined style={{ fontSize: '32px', color: '#52c41a', marginBottom: '8px' }} />
                    <Title level={5} style={{ margin: '0 0 8px 0', color: '#52c41a', fontSize: '18px' }}>
                        {winner === playerChar ? '🎉 You Win!' : winner === aiChar ? '😢 You Lost!' : '🤝 It\'s a Draw!'}
                    </Title>
                    <div style={{ marginBottom: '12px' }}>
                        <Text style={{ 
                            fontSize: '12px', 
                            color: '#666',
                            display: 'block',
                            marginBottom: '8px',
                            fontWeight: 600
                        }}>
                            Difficulty:
                        </Text>
                        <Radio.Group 
                            value={difficulty} 
                            onChange={handleDifficultyChange}
                            buttonStyle="solid"
                            size="small"
                            style={{ 
                                display: 'flex',
                                justifyContent: 'center',
                                gap: '4px',
                                flexWrap: 'wrap'
                            }}
                        >
                            <Radio.Button value="easy" style={{ 
                                borderRadius: '4px',
                                fontSize: '11px',
                                padding: '2px 8px',
                                height: '28px',
                                lineHeight: '24px',
                                border: difficulty === 'easy' ? '2px solid #52c41a' : '1px solid #d9d9d9'
                            }}>
                                😊 Easy
                            </Radio.Button>
                            <Radio.Button value="medium" style={{ 
                                borderRadius: '4px',
                                fontSize: '11px',
                                padding: '2px 8px',
                                height: '28px',
                                lineHeight: '24px',
                                border: difficulty === 'medium' ? '2px solid #1890ff' : '1px solid #d9d9d9'
                            }}>
                                🤔 Medium
                            </Radio.Button>
                            <Radio.Button value="hard" style={{ 
                                borderRadius: '4px',
                                fontSize: '11px',
                                padding: '2px 8px',
                                height: '28px',
                                lineHeight: '24px',
                                border: difficulty === 'hard' ? '2px solid #ff4d4f' : '1px solid #d9d9d9'
                            }}>
                                🔥 Hard
                            </Radio.Button>
                        </Radio.Group>
                    </div>
                    <Text style={{ 
                        fontSize: '11px', 
                        color: '#666',
                        display: 'block',
                        marginTop: '8px',
                        fontStyle: 'italic'
                    }}>
                        New game starting...
                    </Text>
                </div>
            )}

            {/* Difficulty Indicator */}
            <div className="difficulty-indicator" style={{
                marginTop: '0',
                marginBottom: '6px',
                textAlign: 'center',
                padding: '4px 8px',
                background: difficulty === 'easy' ? 'rgba(82, 196, 26, 0.1)' : 
                           difficulty === 'medium' ? 'rgba(24, 144, 255, 0.1)' : 
                           'rgba(255, 77, 79, 0.1)',
                borderRadius: '6px',
                border: `1px solid ${difficulty === 'easy' ? 'rgba(82, 196, 26, 0.3)' : 
                                    difficulty === 'medium' ? 'rgba(24, 144, 255, 0.3)' : 
                                    'rgba(255, 77, 79, 0.3)'}`
            }}>
                <Text style={{ 
                    fontSize: '11px', 
                    color: difficulty === 'easy' ? '#52c41a' : 
                           difficulty === 'medium' ? '#1890ff' : '#ff4d4f',
                    fontWeight: 600
                }}>
                    {difficulty === 'easy' ? '😊 Easy Mode' : 
                     difficulty === 'medium' ? '🤔 Medium Mode' : '🔥 Hard Mode'}
                </Text>
            </div>
            
            {/* Score Board */}
            <div className="score-board" style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '8px',
                padding: '6px 10px',
                background: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '10px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                border: '1px solid rgba(24, 144, 255, 0.2)'
            }}>
                <div style={{ textAlign: 'center', flex: 1 }}>
                    <Text className="score-label" style={{ fontSize: '11px', color: '#666', display: 'block', fontWeight: 600 }}>
                        You
                    </Text>
                    <Text className="score-value" style={{ fontSize: '20px', fontWeight: 'bold', color: '#1890ff' }}>
                        {playerScore}
                    </Text>
                </div>
                <div style={{ textAlign: 'center', flex: 1 }}>
                    <Text className="score-status" style={{ fontSize: '11px', color: '#1890ff', fontWeight: 700 }}>
                        {getStatus()}
                    </Text>
                </div>
                <div style={{ textAlign: 'center', flex: 1 }}>
                    <Text className="score-label" style={{ fontSize: '11px', color: '#666', display: 'block', fontWeight: 600 }}>
                        AI
                    </Text>
                    <Text className="score-value" style={{ fontSize: '20px', fontWeight: 'bold', color: '#52c41a' }}>
                        {aiScore}
                    </Text>
                </div>
            </div>

            {/* Game Board */}
            <div className="tic-tac-toe-board" style={{
                opacity: showResults ? 0.4 : 1,
                transition: 'opacity 0.3s ease',
                pointerEvents: showResults ? 'none' : 'auto'
            }}>
                <div className="board-row">
                    {renderSquare(0)}
                    {renderSquare(1)}
                    {renderSquare(2)}
                </div>
                <div className="board-row">
                    {renderSquare(3)}
                    {renderSquare(4)}
                    {renderSquare(5)}
                </div>
                <div className="board-row">
                    {renderSquare(6)}
                    {renderSquare(7)}
                    {renderSquare(8)}
                </div>
            </div>

            <style jsx>{`
                .difficulty-card {
                    user-select: none;
                }
                
                .difficulty-card:hover {
                    transform: translateY(-4px) scale(1.02) !important;
                    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.15) !important;
                }
                
                .difficulty-card:hover > div:first-child {
                    transform: scale(1.15) rotate(5deg) !important;
                    transition: transform 0.3s ease !important;
                }
                
                .difficulty-card.selected:hover {
                    transform: translateY(-4px) scale(1.03) !important;
                }
                
                .difficulty-card:active {
                    transform: translateY(-1px) scale(0.98) !important;
                }
                
                .difficulty-card.selected > div:last-child {
                    animation: checkmark-appear 0.3s ease-out;
                }
                
                @keyframes checkmark-appear {
                    0% {
                        transform: scale(0) rotate(-180deg);
                        opacity: 0;
                    }
                    100% {
                        transform: scale(1) rotate(0deg);
                        opacity: 1;
                    }
                }
                
                @media (max-width: 576px) {
                    .difficulty-card {
                        min-width: 90px !important;
                        max-width: 120px !important;
                        padding: 12px 8px !important;
                    }
                    
                    .difficulty-card > div:first-child {
                        font-size: 28px !important;
                        margin-bottom: 6px !important;
                    }
                    
                    .difficulty-card > div:nth-child(2) {
                        font-size: 13px !important;
                    }
                    
                    .difficulty-card > div:nth-child(3) {
                        font-size: 10px !important;
                    }
                }
                
                .tic-tac-toe-board {
                    display: flex;
                    flex-direction: column;
                    gap: 6px;
                    background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
                    padding: 10px;
                    border-radius: 12px;
                    border: 2px solid rgba(24, 144, 255, 0.3);
                }
                
                .board-row {
                    display: flex;
                    gap: 6px;
                }
                
                .square {
                    flex: 1;
                    aspect-ratio: 1;
                    background: white;
                    border: 2px solid #1890ff;
                    border-radius: 10px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 48px;
                    font-weight: bold;
                    transition: all 0.3s ease;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
                    min-height: 80px;
                    max-height: 120px;
                }
                
                .square:hover:not(:disabled) {
                    transform: scale(1.05);
                    box-shadow: 0 4px 12px rgba(24, 144, 255, 0.3);
                    background: linear-gradient(135deg, #e6f7ff 0%, #bae7ff 100%);
                }
                
                .square:disabled {
                    cursor: not-allowed;
                    opacity: 0.8;
                }
                
                .square.filled {
                    cursor: default;
                }
                
                .square.winning {
                    background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
                    border-color: #52c41a;
                    animation: winning-pulse 0.6s ease-in-out;
                }
                
                .square.winning-ai {
                    background: linear-gradient(135deg, #f8d7da 0%, #f5c6cb 100%);
                    border-color: #ff4d4f;
                    animation: winning-pulse 0.6s ease-in-out;
                }
                
                .x-mark {
                    color: #1890ff;
                    font-size: 56px;
                    font-weight: bold;
                    line-height: 1;
                }
                
                .o-mark {
                    color: #52c41a;
                    font-size: 56px;
                    font-weight: bold;
                    line-height: 1;
                }
                
                @keyframes winning-pulse {
                    0%, 100% { transform: scale(1); }
                    50% { transform: scale(1.1); }
                }
                
                @media (max-width: 768px) {
                    .tic-tac-toe-board {
                        gap: 4px;
                        padding: 8px;
                    }
                    
                    .board-row {
                        gap: 4px;
                    }
                    
                    .square {
                        min-height: 70px;
                        max-height: 100px;
                        border-width: 2px;
                        font-size: 40px;
                    }
                    
                    .x-mark {
                        font-size: 48px;
                    }
                    
                    .o-mark {
                        font-size: 48px;
                    }
                }
                
                @media (max-width: 576px) {
                    .tic-tac-toe-board {
                        gap: 3px;
                        padding: 6px;
                    }
                    
                    .board-row {
                        gap: 3px;
                    }
                    
                    .square {
                        min-height: 55px;
                        max-height: 80px;
                        border-width: 2px;
                        font-size: 32px;
                    }
                    
                    .x-mark {
                        font-size: 38px;
                    }
                    
                    .o-mark {
                        font-size: 38px;
                    }
                    
                    .difficulty-indicator {
                        margin-bottom: 6px !important;
                        padding: 4px 8px !important;
                    }
                    
                    .difficulty-indicator .ant-typography {
                        font-size: 10px !important;
                    }
                    
                    .score-board {
                        margin-bottom: 8px !important;
                        padding: 6px 10px !important;
                    }
                    
                    .score-board .score-value {
                        font-size: 18px !important;
                    }
                    
                    .score-board .score-label {
                        font-size: 10px !important;
                    }
                    
                    .score-board .score-status {
                        font-size: 10px !important;
                    }
                    
                    .game-over-actions {
                        margin-top: 6px !important;
                    }
                    
                    .game-over-actions > div {
                        margin-bottom: 6px !important;
                    }
                    
                    .game-over-actions .ant-typography {
                        font-size: 10px !important;
                        margin-bottom: 4px !important;
                    }
                    
                    .game-over-actions .ant-btn {
                        height: 32px !important;
                        font-size: 12px !important;
                        padding: 0 14px !important;
                    }
                    
                    .game-over-actions .ant-radio-group {
                        gap: 3px !important;
                    }
                    
                    .game-over-actions .ant-radio-button-wrapper {
                        font-size: 10px !important;
                        padding: 2px 6px !important;
                        height: 26px !important;
                        line-height: 22px !important;
                    }
                }
                
                @media (max-width: 768px) {
                    .difficulty-indicator {
                        margin-bottom: 6px !important;
                        padding: 4px 8px !important;
                    }
                    
                    .difficulty-indicator .ant-typography {
                        font-size: 10px !important;
                    }
                    
                    .score-board {
                        margin-bottom: 6px !important;
                        padding: 6px 8px !important;
                    }
                    
                    .score-board .score-value {
                        font-size: 16px !important;
                    }
                    
                    .score-board .score-label {
                        font-size: 9px !important;
                    }
                    
                    .score-board .score-status {
                        font-size: 9px !important;
                    }
                    
                    .game-over-actions {
                        margin-top: 6px !important;
                    }
                    
                    .game-over-actions > div {
                        margin-bottom: 6px !important;
                    }
                    
                    .game-over-actions .ant-typography {
                        font-size: 10px !important;
                        margin-bottom: 4px !important;
                    }
                    
                    .game-over-actions .ant-btn {
                        height: 32px !important;
                        font-size: 12px !important;
                        padding: 0 14px !important;
                    }
                    
                    .game-over-actions .ant-radio-group {
                        gap: 3px !important;
                    }
                    
                    .game-over-actions .ant-radio-button-wrapper {
                        font-size: 10px !important;
                        padding: 2px 6px !important;
                        height: 26px !important;
                        line-height: 22px !important;
                    }
                }
            `}</style>
        </div>
    );
};

export default TicTacToe;

